import { Box, Button, Flex, Stack } from '@chakra-ui/react';
import { Permission } from 'api/types';
import Link from 'components/Link';
import { displayRow } from 'helpers/general';
import useOPUserQuery from 'hooks/useOPUserQuery';
import { ContentCard } from 'layout/ContentCard';
import { useTranslation } from 'react-i18next';
import { CgProfile } from 'react-icons/cg';
import { useNavigate, useParams } from 'react-router-dom';

import { UserDangerZone } from './UserDangerZone';

const UserDetailsPage = () => {
  const { id } = useParams();

  const userId = Number(id);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const userQuery = useOPUserQuery(userId);
  const user = userQuery.data;

  const timezone = userQuery.data?.timezone || '-';
  const portalLanguage = userQuery.data?.language || '-';

  const userRoles = userQuery.data?.roles || [];
  const userRolesMapped = userRoles.reduce((accumulator: string[], role) => {
    const roleName = role.label;
    accumulator.push(roleName);
    return accumulator;
  }, []);

  const userRolesWithComma = userRolesMapped.map((role) => (userRolesMapped.indexOf(role) + 1 === userRolesMapped.length ? role : role + ', ')) ?? [];

  const spokenLanguages = userQuery.data?.spoken_languages || [];
  const spokenLanguagesWithComma =
    spokenLanguages.map((language: string) => (spokenLanguages.indexOf(language) + 1 === spokenLanguages.length ? language : language + ', ')) ?? [];

  const additionalPermissions = (userQuery.data?.permissions || []) as unknown as Permission[];
  const additionalPermissionsMapped = additionalPermissions.reduce((accumulator, permission) => {
    const permissionName = permission.label;
    accumulator.push(permissionName);
    return accumulator;
  }, [] as string[]);

  const additionalPermissionsWithComma =
    additionalPermissionsMapped.map((permission) =>
      additionalPermissionsMapped.indexOf(permission) + 1 === additionalPermissionsMapped.length ? permission : permission + ', ',
    ) ?? [];

  if (userQuery.isError) {
    navigate('/404', { replace: true });
    return null;
  }

  return (
    <Box maxWidth="120ch">
      {user != null && (
        <>
          <ContentCard isLoading={userQuery.isFetching} icon={<CgProfile size="40" />} header={t('user:details')}>
            <Stack>
              {displayRow({
                label: t('user:name'),
                content: user.name,
                isLoading: userQuery.isLoading,
              })}
              {displayRow({
                label: t('user:email'),
                content: user.email,
                isLoading: userQuery.isLoading,
              })}

              {displayRow({
                label: t('user:personal_settings.language.label'),
                content: portalLanguage,
                isLoading: userQuery.isLoading,
              })}

              {spokenLanguagesWithComma.length > 0 &&
                displayRow({
                  label: t('user:personal_settings.spoken_languages.label'),
                  content: spokenLanguagesWithComma,
                  isLoading: userQuery.isLoading,
                })}

              {displayRow({
                label: t('user:personal_settings.timezone'),
                content: timezone,
                isLoading: userQuery.isLoading,
              })}

              {userRoles?.length > 0 &&
                displayRow({
                  label: t('user:roles'),
                  content: userRolesWithComma,
                  isLoading: userQuery.isLoading,
                })}

              {additionalPermissions?.length > 0 &&
                displayRow({
                  label: t('user:roles_and_permissions.permissions.additional'),
                  content: additionalPermissionsWithComma,
                  isLoading: userQuery.isLoading,
                })}

              <Flex direction="row-reverse">
                <Button variant="primary" as={Link} to={`/app/op/user/update/${userId}`}>
                  {t('common:edit')}
                </Button>
              </Flex>
            </Stack>
          </ContentCard>

          <Box marginTop={8}>
            <UserDangerZone user={user} />
          </Box>
        </>
      )}
    </Box>
  );
};
export default UserDetailsPage;
